import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { subscriptionsService } from '../../lib/api/subscriptions';
import { Logo } from '../../components/common/Logo';

/**
 * CheckoutSuccessPage
 * 
 * Architecture: Webhook-first
 * 
 * 1. PRIMARY: Poll GET /subscriptions — the webhook should have already created it
 * 2. FALLBACK: After ~16 seconds of polling, try sync-session as emergency backup
 * 3. TIMEOUT: After ~30 seconds total, show "still processing" message
 * 4. ERROR: 403/404 from sync-session → show specific, actionable error messages
 */
const CheckoutSuccessPage = () => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const syncAttemptedRef = useRef(false);
    const attemptsRef = useRef(0);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        const maxPollingAttempts = 8;    // 8 polls × 2s = 16 seconds of webhook-first polling
        const maxTotalAttempts = 15;     // 15 total × 2s = 30 seconds before timeout
        const pollInterval = 2000;

        const redirectToSuccess = () => {
            navigate('/boards?modal=profile&tab=billing', {
                state: { message: t('checkout.successMessage') }
            });
        };

        const checkSubscription = async () => {
            attemptsRef.current++;
            const currentAttempt = attemptsRef.current;

            try {
                // Step 1: Poll for subscription (webhook should have created it)
                const response = await subscriptionsService.getMySubscriptions();
                if (response.success && Array.isArray(response.data)) {
                    const active = response.data.find(
                        s => s.status === 'active' || s.status === 'trialing'
                    );

                    if (active) {
                        redirectToSuccess();
                        return;
                    }
                }
            } catch (e) {
                console.warn('Subscription poll failed', e);
            }

            // Step 2: After initial polling window, try sync-session as emergency fallback
            if (currentAttempt >= maxPollingAttempts && sessionId && !syncAttemptedRef.current) {
                syncAttemptedRef.current = true;
                try {
                    console.info('Webhook may be delayed, attempting direct session sync');
                    await subscriptionsService.syncSession(sessionId);
                    redirectToSuccess();
                    return;
                } catch (e: any) {
                    const status = e?.response?.status || e?.status;

                    if (status === 403) {
                        // Session belongs to a different user — likely logged into wrong account
                        setErrorMessage(t('checkout.sessionMismatch'));
                        setLoading(false);
                        return;
                    }

                    if (status === 404) {
                        // Session expired or invalid
                        setErrorMessage(t('checkout.sessionExpired'));
                        setLoading(false);
                        return;
                    }

                    console.warn('Emergency sync-session failed, continuing to poll', e);
                }
            }

            // Step 3: Continue polling or timeout
            if (currentAttempt < maxTotalAttempts) {
                setTimeout(checkSubscription, pollInterval);
            } else {
                setLoading(false); // Show timeout message
            }
        };

        checkSubscription();
    }, [navigate, searchParams, t]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-neutral-100 to-neutral-50 flex flex-col items-center justify-center p-4">
            {/* Decorative elements */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
                <div className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-radial from-accent-primary/5 to-transparent rounded-full blur-3xl" />
                <div className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-radial from-accent-primary/5 to-transparent rounded-full blur-3xl" />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-neutral-100"
            >
                {loading ? (
                    <>
                        {/* Logo-based loader */}
                        <div className="relative w-20 h-20 mx-auto mb-6">
                            {/* Pulsing background glow */}
                            <motion.div
                                animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                className="absolute inset-0 bg-accent-primary/20 rounded-full blur-xl"
                            />

                            {/* Spinning ring */}
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                className="absolute inset-0 m-auto w-20 h-20 rounded-full border-2 border-transparent border-t-accent-primary border-r-accent-primary/30"
                            />

                            {/* Logo with subtle pulse */}
                            <motion.div
                                animate={{ scale: [1, 1.05, 1] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                <Logo className="w-10 h-10" />
                            </motion.div>
                        </div>

                        <motion.h2
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-2xl font-bold text-text-primary mb-2 font-display"
                        >
                            {t('checkout.processing')}
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="text-neutral-600"
                        >
                            {t('checkout.waitConfirm')}
                        </motion.p>
                    </>
                ) : (
                    <>
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6"
                        >
                            <span className="w-8 h-8" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_55954675.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_55954675.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                        </motion.div>
                        <h2 className="text-2xl font-bold text-text-primary mb-2 font-display">{errorMessage ? t('checkout.errorTitle') : t('checkout.takingLonger')}</h2>
                        <p className="text-neutral-600 mb-6">{errorMessage || t('checkout.couldNotConfirm')}</p>
                        <button
                            onClick={() => navigate('/boards?modal=profile&tab=billing')}
                            className="btn-primary w-full"
                        >
                            {t('checkout.returnToProfile')}
                        </button>
                    </>
                )}
            </motion.div>
        </div>
    );
};

export default CheckoutSuccessPage;
